import { useQuery } from '@apollo/client';
import { gql } from '__generated__/gql';
import { FundClosingDrawdownType } from '__generated__/graphql';
import { FC } from 'react';

import LoadingIndicator from 'primitives/LoadingIndicator';
import SlideOver from 'primitives/SlideOver';

import ErrorMessage from 'components/ErrorMessage';

import constants from 'utils/constants';

import FundClosingAudit from './FundClosingAudit';
import FundClosingDrawdownSection from './FundClosingDrawdownSection';
import FundClosingEmailAudit from './FundClosingEmailAudit';
import RemoveFundClosingButton from './RemoveFundClosingButton';
import UpdateFundClosing from './UpdateFundClosing';
import UpdateFundClosingStatus from './UpdateFundClosingStatus';

const FUND_CLOSING_QUERY = gql(`
  query getFundClosing($id: ID!) {
    fundClosing(id: $id) {
      id
      amount
      fundingSource
      investmentEntity {
        id
        name
      }
      setupFeePercent
      status
      wiredAt
      executedAt
      finalisedAt
      createdAt
      updatedAt
    }
  }
`);

const FUND_CLOSING_DRAWDOWN_QUERY = gql(`
  query getFundClosingDrawdowns($fundClosingId: ID!) {
    fundClosingDrawdowns(fundClosingId: $fundClosingId) {
      id
      amount
      status
      createdAt
      updatedAt
    }
  }
`);

const FundClosingDialog: FC<{
  fundClosingId: string;
  onClose: () => void;
}> = ({ fundClosingId, onClose }) => {
  const { loading, error, data, refetch } = useQuery(FUND_CLOSING_QUERY, {
    variables: {
      id: fundClosingId,
    },
  });

  const {
    loading: drawdownLoading,
    error: drawdownError,
    data: drawdownData,
    refetch: drawdownRefetch,
  } = useQuery(FUND_CLOSING_DRAWDOWN_QUERY, {
    variables: {
      fundClosingId: fundClosingId as string,
    },
  });

  const renderContent = () => {
    if (loading || drawdownLoading) return <LoadingIndicator />;

    if (error || !data || drawdownError || !drawdownData)
      return <ErrorMessage error={error} refetch={refetch} />;

    const fundClosing = data.fundClosing;

    return (
      <>
        <UpdateFundClosing
          id={fundClosing.id}
          amount={fundClosing.amount}
          fundingSource={fundClosing.fundingSource}
          investmentEntity={fundClosing.investmentEntity}
          setupFeePercent={fundClosing.setupFeePercent ?? constants.DEFAULT_SETUP_FEE_PERCENTAGE}
        />

        <FundClosingDrawdownSection
          data={drawdownData.fundClosingDrawdowns as FundClosingDrawdownType[]}
          fundClosingId={fundClosing.id}
          refetch={drawdownRefetch}
        />

        <UpdateFundClosingStatus
          id={fundClosing.id}
          status={fundClosing.status}
          wiredAt={fundClosing.wiredAt}
          executedAt={fundClosing.executedAt}
          finalisedAt={fundClosing.finalisedAt}
        />

        {fundClosing.status !== 'ACTIVE' ? (
          <>
            <RemoveFundClosingButton id={fundClosing.id} onClose={onClose} />
          </>
        ) : null}

        <FundClosingEmailAudit fundClosingId={fundClosing.id} />

        <FundClosingAudit fundClosingId={fundClosing.id} />
      </>
    );
  };

  return (
    <SlideOver
      open={true}
      onClose={onClose}
      title="Fund Commitment Details"
      subTitle="View and manage the details of this fund commitment"
    >
      {renderContent()}
    </SlideOver>
  );
};

export default FundClosingDialog;
